/**
 * The "big three" of a birth chart — Sun, Moon, Rising — and the plumbing the
 * sign cards need.
 *
 * Two surfaces render these cards: the seeker's Mirror (MirrorProfile) and the
 * Mystic's Studio Identity header (ProfilePanel). Both used to hold their own
 * copy of the list, and both mapped a card back to an image subject with the
 * same two-way ternary — `which === 'rising' ? 'rising_sign' : 'sun_sign'` —
 * which silently sends any third sign to the Sun's subject. Keeping the list and
 * the mapping here means the Moon arrives on both surfaces at once, and paints
 * into its own subject rather than over the Sun's.
 *
 * A sign with no value is dropped rather than shown empty. The Moon and the
 * Rising sign both need a birth time and a birthplace, which most seekers have
 * not given us, so "no value" is the ordinary case and not an error.
 */

export const SIGN_ROLES = Object.freeze([
  Object.freeze({ which: 'sun', role: 'Sun' }),
  Object.freeze({ which: 'moon', role: 'Moon' }),
  Object.freeze({ which: 'rising', role: 'Rising' }),
])

function role(which) {
  const found = SIGN_ROLES.find(r => r.which === which)
  return found || SIGN_ROLES[0]
}

/**
 * The cards to render, in Sun → Moon → Rising order.
 *
 * @param {object} chart  `{ sun, moon, rising }` sign names from the server.
 * @param {object} images `{ sun, moon, rising }` custom painted image URLs.
 * @returns {Array<{which: string, role: string, value: string, img: ?string}>}
 */
export function buildSignList(chart, images) {
  const c = chart || {}
  const i = images || {}
  return SIGN_ROLES
    .map(({ which, role: r }) => ({
      which, role: r, value: c[which] || null, img: i[which] || null,
    }))
    .filter(s => s.value)
}

/**
 * The altar-space subject a sign's custom image is painted into. `scope`
 * 'reader' selects the Mystic's brand-facing set, which is deliberately kept
 * apart from their own private seeker signs.
 */
export function signSubject(which, scope) {
  const base = `${role(which).which}_sign`
  return scope === 'reader' ? `reader_${base}` : base
}

/** The builder's title, e.g. "Gemini Moon". */
export function signLabel(sign, which) {
  return [sign, role(which).role].filter(Boolean).join(' ')
}

/** Every subject this module can produce — seeker-side and reader-side. */
export const SIGN_SUBJECTS = Object.freeze(
  SIGN_ROLES.flatMap(r => [signSubject(r.which), signSubject(r.which, 'reader')]),
)

/** Is this altar-space subject one of the sign images (rather than an altar,
 *  keeper, avatar or season image)? */
export function isSignSubject(subject) {
  return SIGN_SUBJECTS.includes(subject)
}
